import React from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

type LayoutProps = {
  children: React.ReactNode;
};

const imgZkpoollogo2 = '/assets/imgZkpoollogo2.png';
const imgSearch = '/assets/imgSearch.svg';

const navItems = [
  { label: 'Home', href: '/' },
  { label: 'Earn', href: '/earn' },
  { label: 'Airdrop', href: '/airdrop' },
  { label: 'Transfer', href: '/transfer' },
];

const NetworkSwitcher = dynamic(() => import('./NetworkSwitcher'), {
  ssr: false,
  loading: () => <div className="h-9 w-[150px] rounded-lg bg-[#151722]/70" />,
});

const WalletConnectButton = dynamic(() => import('./WalletConnectButton'), {
  ssr: false,
  loading: () => <div className="h-9 w-[148px] rounded-lg bg-[#151722]/70" />,
});

export function Layout({ children }: LayoutProps) {
  const { pathname } = useRouter();

  return (
    <div className="relative mx-auto flex w-full flex-col px-0 pt-0 sm:px-0">
      <header className="relative h-[56px] w-full border-b border-[#2b2b2b] bg-[#0c0e13]">
        <div className="mx-auto flex h-full w-full max-w-full items-center justify-between px-4 sm:px-10">
          <div className="flex items-center gap-6 sm:gap-12">
            <div className="flex items-center gap-2">
              <div className="relative h-[36px] w-[36px] shrink-0">
                <img
                  src={imgZkpoollogo2}
                  alt="ZKPool logo"
                  className="absolute inset-0 h-full w-full rounded-full object-cover"
                />
              </div>
              <span className="text-[20px] font-bold leading-none text-[#39e5f5] font-[var(--font-lexend)] whitespace-nowrap">
                ZKPOOL
              </span>
            </div>
            <nav className="hidden items-center gap-4 sm:flex">
              {navItems.map((item) => {
                const isActive = pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`rounded-full px-4 py-2 text-[14px] font-[var(--font-jakarta)] transition-colors whitespace-nowrap ${
                      isActive
                        ? 'text-white'
                        : 'text-[#909090] hover:text-white hover:bg-[#161824]'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 lg:gap-6 shrink-0">
            <div className="hidden h-[36px] items-center gap-2 rounded-[8px] border border-[#2c2e49] bg-[rgba(24,25,44,0.5)] px-3 text-sm text-[#cbcfd6] sm:flex sm:w-[260px] lg:w-[380px]">
              <img src={imgSearch} alt="" className="h-4 w-4 shrink-0" />
              <span className="text-[14px] font-[var(--font-jakarta)] text-[#6b6b6b]">Search</span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <NetworkSwitcher />
              <WalletConnectButton />
            </div>
          </div>
        </div>
      </header>

      <main className="w-full">{children}</main>

      <footer className="relative w-full border-t border-[color:var(--line-gray,#2b2b2b)] bg-[#0c0e13] px-4 py-6 text-center font-[var(--font-jakarta)]">
        <p className="text-[14px] leading-[1.5] text-[color:var(--text-tertiary,#909090)]">
          © 2025 ZKPool. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
